import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, FileText } from 'lucide-react';
import moment from 'moment';
import TransactionDetailModal from './TransactionDetailModal';
import InvoiceDetailModal from '../modals/InvoiceDetailModal';
import LedgerToolbar from '../common/LedgerToolbar';
import useExchangeStore from '../../store/useExchangeStore';
import useAuthStore from '../../store/authStore';
import { formatCurrency } from '../../utils/formatters';
import { getLedgerExchangeRate, convertLedgerAmount } from '../../utils/ledgerExchangeRate';
import { presentRowsBySortDirection } from '../../utils/financeLedgerSort';

const REPORT_CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP'];
const REPORT_LANGUAGES = ['tr', 'en', 'de'];

const normalizeCurrency = (currency = 'TRY') => {
  const normalized = String(currency || 'TRY').trim().toUpperCase();
  return REPORT_CURRENCIES.includes(normalized) ? normalized : 'TRY';
};

const normalizeLanguage = (language = 'tr') => {
  const normalized = String(language || 'tr').trim().toLowerCase();
  return REPORT_LANGUAGES.includes(normalized) ? normalized : 'tr';
};

const formatRate = (value) => {
  const numeric = Number(value || 0);
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numeric);
};

const invoiceTypeLabels = {
  Sales: 'Satış Faturası',
  Purchase: 'Alış Faturası',
  SalesReturn: 'Satış İade Faturası',
  PurchaseReturn: 'Alış İade Faturası'
};

const getTransactionTypeLabel = (row) => {
  const source = String(row?.source || '').trim();
  const type = String(row?.type || '').trim();
  const direction = String(row?.direction || '').trim();
  const category = String(row?.category || '').trim();

  if (source === 'Invoice' || type === 'INVOICE' || row?.invoiceId) {
    return invoiceTypeLabels[row?.description] || invoiceTypeLabels[row?.invoiceType] || invoiceTypeLabels[type] || row?.description || 'Fatura';
  }

  if (category === 'Transfer') return 'Virman';

  if (['Inflow', 'inflow'].includes(type) || ['Inflow', 'inflow'].includes(direction)) return 'Tahsilat';
  if (['Outflow', 'outflow'].includes(type) || ['Outflow', 'outflow'].includes(direction)) return 'Ödeme';

  if (['collection', 'tahsilat'].includes(category.toLowerCase())) return 'Tahsilat';
  if (['payment', 'odeme', 'ödeme'].includes(category.toLowerCase())) return 'Ödeme';

  if (Number(row?.credit || row?.convertedCredit || 0) > Number(row?.debit || row?.convertedDebit || 0)) return 'Tahsilat';
  if (Number(row?.debit || row?.convertedDebit || 0) > Number(row?.credit || row?.convertedCredit || 0)) return 'Ödeme';

  return row?.description || row?.category || row?.type || '-';
};

const getTransactionTypeTone = (row) => {
  const label = getTransactionTypeLabel(row);
  if (label.includes('Tahsilat') || label.includes('Satış')) {
    return 'bg-emerald-50 text-emerald-700';
  }

  if (label.includes('Ödeme') || label.includes('Alış')) {
    return 'bg-rose-50 text-rose-700';
  }

  if (label === 'Virman') {
    return 'bg-slate-100 text-slate-700';
  }

  return 'bg-slate-100 text-slate-700';
};

const getLedgerRows = (ledgerReport) => {
  const sections = Array.isArray(ledgerReport?.sections) ? ledgerReport.sections : [];
  return sections.flatMap((section) => (
    Array.isArray(section?.rows)
      ? section.rows.map((row) => ({
          ...row,
          currency: normalizeCurrency(row.currency || section.currency || 'TRY')
        }))
      : []
  ));
};

const getLedgerDateBounds = (ledgerReport) => {
  const rows = getLedgerRows(ledgerReport);
  const validDates = rows
    .map((row) => moment(row.date || row.createdAt))
    .filter((date) => date.isValid())
    .sort((a, b) => a.valueOf() - b.valueOf());

  return {
    startDate: validDates[0] ? validDates[0].format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')
  };
};

const toSortableTimestamp = (value) => {
  const timestamp = new Date(value || 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

// Ledger balances depend on a deterministic source order. `date` is the
// business date, while `createdAt` preserves the order of records entered on
// the same day; sourceId/_id is the final stable tie breaker.
const compareLedgerRowsChronologically = (left, right) => {
  const dateDiff = toSortableTimestamp(left.date) - toSortableTimestamp(right.date);
  if (dateDiff !== 0) return dateDiff;

  const createdAtDiff = toSortableTimestamp(left.createdAt) - toSortableTimestamp(right.createdAt);
  if (createdAtDiff !== 0) return createdAtDiff;

  const leftId = String(left._id || left.sourceId || left.documentNo || '');
  const rightId = String(right._id || right.sourceId || right.documentNo || '');
  return leftId.localeCompare(rightId);
};

const buildConsolidatedLedger = (ledgerReport, reportCurrency, rates = {}, dateRange = {}, sortDirection = 'desc') => {
  const selectedCurrency = normalizeCurrency(reportCurrency);
  const allRows = getLedgerRows(ledgerReport);
  const startMoment = dateRange.startDate ? moment(dateRange.startDate).startOf('day') : null;
  const endMoment = dateRange.endDate ? moment(dateRange.endDate).endOf('day') : null;

  // Running balance is calculated from the oldest visible movement. It must not
  // inherit the summary opening value, otherwise rows can be displayed with an
  // inverse/duplicated balance when the visual order is newest first.
  const openingBalance = 0;

  const filteredRows = allRows
    .filter((row) => {
      const rowMoment = moment(row.date || row.createdAt);
      if (!rowMoment.isValid()) return true;
      if (startMoment && rowMoment.isBefore(startMoment)) return false;
      if (endMoment && rowMoment.isAfter(endMoment)) return false;
      return true;
    });

  const chronologicalRows = filteredRows
    .map((row) => {
      const sourceCurrency = normalizeCurrency(row.currency || 'TRY');
      const debit = Number(row.debit || 0);
      const credit = Number(row.credit || 0);
      const originalAmount = debit > 0 ? debit : credit > 0 ? credit : 0;
      const exchangeRate = getLedgerExchangeRate(row, sourceCurrency, selectedCurrency, rates);
      // TRY amounts are divided by the reporting currency's TRY quote.
      // Display that quote rather than the small multiplication coefficient.
      const displayRate = sourceCurrency === 'TRY' && selectedCurrency !== 'TRY'
        ? 1 / exchangeRate
        : exchangeRate;
      const convertedDebit = convertLedgerAmount(debit, exchangeRate);
      const convertedCredit = convertLedgerAmount(credit, exchangeRate);

      return {
        ...row,
        sourceCurrency,
        reportCurrency: selectedCurrency,
        originalAmount,
        convertedDebit,
        convertedCredit,
        exchangeRate,
        originalAmountLabel: sourceCurrency === selectedCurrency
          ? formatCurrency(originalAmount, sourceCurrency)
          : `${formatCurrency(originalAmount, sourceCurrency)} @ ${formatRate(displayRate)}`
      };
    })
    .sort(compareLedgerRowsChronologically);

  const rowsWithRunningBalance = chronologicalRows.reduce((acc, row) => {
    const runningBalance = Math.round((acc.runningBalance + row.convertedDebit - row.convertedCredit) * 100) / 100;
    acc.rows.push({
      ...row,
      runningBalance
    });
    acc.runningBalance = runningBalance;
    return acc;
  }, { rows: [], runningBalance: 0 }).rows;

  // Keep each row's chronologically calculated balance, then present rows
  // in the user-selected visual order.
  const rows = presentRowsBySortDirection(rowsWithRunningBalance, sortDirection);

  const totals = rows.reduce((acc, row) => ({
    totalDebit: acc.totalDebit + row.convertedDebit,
    totalCredit: acc.totalCredit + row.convertedCredit,
    netBalance: acc.netBalance + row.convertedDebit - row.convertedCredit
  }), {
    totalDebit: 0,
    totalCredit: 0,
    netBalance: openingBalance
  });

  return {
    reportCurrency: selectedCurrency,
    openingBalance,
    rows,
    totalDebit: totals.totalDebit,
    totalCredit: totals.totalCredit,
    netBalance: totals.netBalance,
    closingBalance: totals.netBalance
  };
};

const AccountTransactions = ({
  accountData,
  ledgerReport = null,
  onExport
}) => {
  const { fetchRates, rates } = useExchangeStore();
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const [ledgerLanguage, setLedgerLanguage] = useState('tr');
  const [reportCurrency, setReportCurrency] = useState('TRY');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTxId, setSelectedTxId] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  useEffect(() => {
    fetchRates?.();
  }, [fetchRates]);

  const ledgerDateBounds = useMemo(() => getLedgerDateBounds(ledgerReport), [ledgerReport]);
  const defaultStartDate = ledgerDateBounds.startDate;
  const defaultEndDate = moment().format('YYYY-MM-DD');
  const resolvedStartDate = startDate || defaultStartDate;
  const resolvedEndDate = endDate || defaultEndDate;

  const consolidatedLedger = useMemo(() => {
    if (!ledgerReport?.sections && !ledgerReport?.summary) return null;
    return buildConsolidatedLedger(ledgerReport, reportCurrency, rates, {
      startDate: resolvedStartDate,
      endDate: resolvedEndDate
    }, sortDirection);
  }, [ledgerReport, rates, reportCurrency, resolvedStartDate, resolvedEndDate, sortDirection]);

  const handleRowClick = (transactionId) => {
    setSelectedTxId(transactionId);
    setIsModalOpen(true);
  };

  const handleInvoiceClick = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setIsInvoiceModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedTxId(null);
  };

  const handleExport = () => {
    if (!onExport || !consolidatedLedger) return;

    onExport({
      account: accountData,
      reportCurrency: consolidatedLedger.reportCurrency,
      ledger: consolidatedLedger,
      lang: normalizeLanguage(ledgerLanguage),
      startDate: resolvedStartDate,
      endDate: resolvedEndDate
    });
  };

  if (!consolidatedLedger) {
    return (
      <div className="rounded-3xl border border-dashed border-slate-200 bg-white p-10 text-center text-slate-400 font-bold">
        Hesap ekstresi yükleniyor...
      </div>
    );
  }

  const statusLabel = consolidatedLedger.netBalance > 0
    ? 'Borçlu'
    : consolidatedLedger.netBalance < 0
      ? 'Alacaklı'
      : 'Dengede';

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm">
      <div className="grid gap-3 border-b border-slate-100 bg-slate-50/40 px-5 py-4 md:grid-cols-4">
        <div className="rounded-2xl bg-white p-4 shadow-sm">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Raporlama Dövizi</p>
          <div className="relative mt-1">
            <select
              value={reportCurrency}
              onChange={(event) => setReportCurrency(event.target.value)}
              className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3 py-1.5 pr-8 text-lg font-black text-slate-900 outline-none"
            >
              {REPORT_CURRENCIES.map((currency) => (
                <option key={currency} value={currency}>{currency}</option>
              ))}
            </select>
            <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
          </div>
        </div>
        <div className="rounded-2xl bg-white p-4 shadow-sm">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Toplam Borç</p>
          <p className="mt-1 text-lg font-black text-rose-600">{formatCurrency(consolidatedLedger.totalDebit, consolidatedLedger.reportCurrency)}</p>
        </div>
        <div className="rounded-2xl bg-white p-4 shadow-sm">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Toplam Alacak</p>
          <p className="mt-1 text-lg font-black text-emerald-600">{formatCurrency(consolidatedLedger.totalCredit, consolidatedLedger.reportCurrency)}</p>
        </div>
        <div className="rounded-2xl bg-white p-4 shadow-sm">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Net Bakiye</p>
          <p className={`mt-1 text-lg font-black ${consolidatedLedger.netBalance > 0 ? 'text-blue-600' : consolidatedLedger.netBalance < 0 ? 'text-rose-600' : 'text-slate-800'}`}>
            {formatCurrency(consolidatedLedger.netBalance, consolidatedLedger.reportCurrency)}
          </p>
          <p className="mt-1 text-[10px] font-black uppercase tracking-widest text-slate-400">{statusLabel}</p>
        </div>
      </div>

      <LedgerToolbar
        sortDirection={sortDirection}
        onSortDirectionChange={setSortDirection}
        startDate={resolvedStartDate}
        onStartDateChange={setStartDate}
        endDate={resolvedEndDate}
        onEndDateChange={setEndDate}
        pdfLanguage={ledgerLanguage}
        onPdfLanguageChange={setLedgerLanguage}
        onExport={handleExport}
      />

      <div className="overflow-auto">
        <table className="min-w-full text-left">
          <thead className="sticky top-0 bg-slate-50">
            <tr className="border-b border-slate-100">
              <th className="p-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Tarih</th>
              <th className="p-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Belge No</th>
              <th className="p-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Açıklama</th>
              <th className="p-4 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">İşlem Dövizi / Orijinal Tutar</th>
              <th className="p-4 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Borç</th>
              <th className="p-4 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Alacak</th>
              <th className="p-4 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Yürüyen Bakiye</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {consolidatedLedger.rows.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-16 text-center">
                  <div className="flex flex-col items-center gap-3 opacity-30">
                    <FileText size={44} className="text-slate-400" />
                    <span className="text-xs font-black uppercase tracking-widest text-slate-400">Bu tarih aralığında hareket bulunamadı</span>
                  </div>
                </td>
              </tr>
            ) : consolidatedLedger.rows.map((row) => {
              const isTransactionRow = row.source === 'Transaction';
              const invoiceId = row.invoiceId?._id || row.invoiceId || (row.source === 'Invoice' ? row.sourceId : null);
              const isInvoiceRow = row.source === 'Invoice' || row.type === 'INVOICE' || Boolean(row.invoiceId);

              return (
                <tr
                  key={`${row.source}-${row.sourceId}-${row.documentNo}`}
                  onClick={isTransactionRow
                    ? () => handleRowClick(row.sourceId)
                    : isInvoiceRow && invoiceId
                      ? () => handleInvoiceClick(invoiceId)
                      : undefined}
                  className={`text-sm transition-colors ${
                    isTransactionRow
                      ? 'cursor-pointer hover:bg-indigo-50/50'
                      : isInvoiceRow
                        ? 'cursor-pointer hover:bg-blue-50/50'
                        : ''
                  }`}
                >
                  <td className="p-4 font-bold text-slate-700">
                    {row.date ? moment(row.date).format('DD.MM.YYYY') : '-'}
                  </td>
                  <td className={`p-4 font-black text-slate-700 ${isInvoiceRow ? 'cursor-pointer hover:text-blue-600' : ''}`}>
                    {isInvoiceRow ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (invoiceId) handleInvoiceClick(invoiceId);
                        }}
                        className="text-left font-black text-slate-700 hover:text-blue-600"
                      >
                        {row.documentNo || '-'}
                      </button>
                    ) : (
                      row.documentNo || '-'
                    )}
                  </td>
                  <td className={`p-4 ${isInvoiceRow ? 'cursor-pointer hover:text-blue-600' : ''}`}>
                    {isInvoiceRow ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (invoiceId) handleInvoiceClick(invoiceId);
                        }}
                        className="text-left"
                      >
                        <div className="font-bold text-slate-900">{row.description || row.category || row.type || '-'}</div>
                        <div className={`mt-1 inline-flex rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${getTransactionTypeTone(row)}`}>
                          {getTransactionTypeLabel(row)}
                        </div>
                      </button>
                    ) : (
                      <>
                        <div className="font-bold text-slate-900">{row.description || row.category || row.type || '-'}</div>
                        <div className={`mt-1 inline-flex rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${getTransactionTypeTone(row)}`}>
                          {getTransactionTypeLabel(row)}
                        </div>
                      </>
                    )}
                  </td>
                  <td className="p-4 text-right font-black text-slate-700">
                    {row.originalAmount > 0 ? row.originalAmountLabel : '-'}
                  </td>
                  <td className="p-4 text-right font-black text-rose-600">
                    {row.convertedDebit > 0 ? formatCurrency(row.convertedDebit, consolidatedLedger.reportCurrency) : '-'}
                  </td>
                  <td className="p-4 text-right font-black text-emerald-600">
                    {row.convertedCredit > 0 ? formatCurrency(row.convertedCredit, consolidatedLedger.reportCurrency) : '-'}
                  </td>
                  <td className={`p-4 text-right font-black ${row.runningBalance >= 0 ? 'text-slate-900' : 'text-rose-600'}`}>
                    {formatCurrency(row.runningBalance, consolidatedLedger.reportCurrency)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <TransactionDetailModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        transactionId={selectedTxId}
        canUnlink={hasPermission('invoiceClearing', 'delete')}
      />

      <InvoiceDetailModal
        isOpen={isInvoiceModalOpen}
        onClose={() => {
          setIsInvoiceModalOpen(false);
          setSelectedInvoiceId(null);
        }}
        invoiceId={selectedInvoiceId}
      />
    </div>
  );
};

export default AccountTransactions;
